<script setup>
import { ref, computed, onMounted } from "vue";
import { useDateFilter } from "~/composables/useDateFilter";
import { useAuth } from "~/composables/useAuth";
import { useJackpot } from "~/composables/useJackpot";
import { useBalance } from "~/composables/useBalance";

definePageMeta({
  layout: "mypage",
});

// ===== Composables =====
const {
  dateOptions: options,
  selectedType,
  selectRef,
  handleSelectChange,
  applyDateFilter,
} = useDateFilter();

const { currentUser } = useAuth();
const { getJackpotInfo, getJackpotHistory } = useJackpot();
const { withdraw, getTransactionHistory } = useBalance();

// ===== 상태 관리 =====
// 보유 머니 (USDT)
const balance = computed(() => currentUser.value?.balance || 0);
// 출금 입력 필드에 입력된 값 (문자열, 소수점 포함 가능)
const withdrawAmount = ref("");
// 출금 가능 금액 (보유 머니와 동일)
const maxWithdrawAmount = computed(() => balance.value);
// 활성화된 필터 ('all' | 'deposit' | 'withdraw')
const activeFilter = ref("all");
// 잭팟 정보
const weeklyJackpotAmount = ref(0);
const userJackpotRank = ref(0);
const jackpotRankChange = ref("up");
const jackpotRankChangeRate = ref(0);
// 입/출금 내역 목록
const transactionHistory = ref([]);
// 로딩 상태
const isLoading = ref(false);

// ===== 데이터 로딩 함수 =====
const loadJackpotInfo = async () => {
  const result = await getJackpotInfo();
  if (result.success && result.jackpot) {
    weeklyJackpotAmount.value = result.jackpot.weekly_amount;
    userJackpotRank.value = result.jackpot.user_rank;
    jackpotRankChange.value = result.jackpot.rank_change;
    jackpotRankChangeRate.value = result.jackpot.rank_change_rate;
  }
};

const loadTransactionHistory = async () => {
  isLoading.value = true;
  const result = await getJackpotHistory(100);
  if (result.success && result.history) {
    transactionHistory.value = result.history;
  }
  isLoading.value = false;
};

// 컴포넌트 마운트 시 데이터 로드
onMounted(() => {
  loadJackpotInfo();
  loadTransactionHistory();
});

// ===== 계산된 값 =====
// 필터링된 입/출금 내역 (타입 필터 + 날짜 필터 적용)
const filteredTransactionHistory = computed(() => {
  let filtered = transactionHistory.value;

  // 타입 필터링 (전체/입금/출금)
  if (activeFilter.value !== "all") {
    filtered = filtered.filter(
      (transaction) => transaction.type === activeFilter.value
    );
  }

  // 날짜 필터링 적용
  filtered = applyDateFilter(filtered);

  return filtered;
});

// ===== 함수 =====
// 원화 포맷팅 함수 (천 단위 콤마 추가)
const formatKRW = (amount) => {
  return Math.round(amount).toLocaleString("ko-KR");
};

// 필터 버튼 클릭 핸들러 (전체/입금/출금)
const handleFilterClick = (filter) => {
  activeFilter.value = filter;
};

// 출금 금액 입력 핸들러 (원화 - 정수만 입력 가능, 최대값 제한, 콤마 자동 추가)
const handleWithdrawInput = (event) => {
  let value = event.target.value;
  // 콤마 제거하고 숫자만 추출
  value = value.replace(/[^\d]/g, "");
  let numValue = parseInt(value) || 0;

  // 보유 머니를 초과하지 않도록 제한
  if (numValue > maxWithdrawAmount.value) {
    numValue = maxWithdrawAmount.value;
  }

  // 콤마 포맷팅된 문자열로 변환
  const formattedValue = formatKRW(numValue);
  withdrawAmount.value = numValue.toString();
  event.target.value = formattedValue;
};

// 출금 요청 핸들러
const handleWithdrawRequest = async () => {
  if (!withdrawAmount.value || parseInt(withdrawAmount.value) <= 0) {
    alert('출금 금액을 입력해주세요.');
    return;
  }

  const amount = parseInt(withdrawAmount.value);

  if (amount < 10000) {
    alert('최소 출금 금액은 10,000원입니다.');
    return;
  }

  if (amount > balance.value) {
    alert('출금 가능 금액을 초과했습니다.');
    return;
  }

  isLoading.value = true;
  const result = await withdraw(amount);

  if (result.success) {
    currentUser.value.balance = result.balance;
    withdrawAmount.value = '';
    alert('출금 요청이 완료되었습니다.');
    await loadTransactionHistory();
  } else {
    alert(result.message || '출금 요청에 실패했습니다.');
  }

  isLoading.value = false;
};
</script>

<template>
  <div class="mypage__wallet">
    <!-- ===== 총 잔고 영역 ===== -->
    <div class="mypage__wallet-item">
      <strong class="mypage__wallet-title">총 잔고</strong>
      <div class="mypage__wallet-balance">
        <img src="/images/add_funds.svg" alt="₩" />
        <strong class="mypage__wallet-amount">{{
          formatKRW(balance.value || balance)
        }}원</strong>
      </div>
      <!-- 잭팟 정보 영역 - 사용 안함 -->
      <!-- <div class="mypage__wallet-jackpot"> -->
        <!-- 금주 잭팟 예상 금액 -->
        <!-- <div class="mypage__wallet-jackpot-item">
          <span class="mypage__wallet-jackpot-title">금주 잭팟 예상 금액</span>
          <p>
            <img src="/images/usdt.svg" alt="USDT" />
            <strong class="mypage__wallet-jackpot-amount">{{ formatAmount(weeklyJackpotAmount) }}</strong>
          </p>
        </div> -->

        <!-- 내 잭팟 당첨 순위 (순위 변화 표시) -->
        <!-- <div class="mypage__wallet-jackpot-item">
          <span class="mypage__wallet-jackpot-title">내 잭팟 당첨 순위</span>
          <p>
            <strong class="mypage__wallet-jackpot-rank">{{ userJackpotRank }}위</strong> -->
            <!-- 순위 변화 아이콘 (up/down) -->
            <!-- <span
              :class="[
                'badge',
                `badge--${jackpotRankChange}`,
                'badge--chart',
                'ico-before',
                `ico-${jackpotRankChange}`,
              ]"
            ></span> -->
            <!-- 순위 변화율 (+, - 표시) -->
            <!-- <span
              :class="['badge', `badge--${jackpotRankChange}`, 'badge--sm']"
            >
              {{ jackpotRankChange === "up" ? "+" : "-"
              }}{{ jackpotRankChangeRate }}%
            </span>
          </p>
        </div>
      </div> -->
    </div>

    <!-- ===== 출금 요청 영역 ===== -->
    <div class="mypage__wallet-item">
      <strong class="mypage__wallet-title">출금 요청</strong>
      <div class="mypage__wallet-form">
        <input
          type="text"
          class="input-text input-md"
          placeholder="0"
          :value="withdrawAmount ? formatKRW(parseInt(withdrawAmount) || 0) : ''"
          @input="handleWithdrawInput"
        />
        <span class="mypage__wallet-form-prefix">원</span>
      </div>
      <div class="mypage__wallet-form-info">
        <p>
          <span>출금가능:</span>
          <strong>₩{{ formatKRW(maxWithdrawAmount) }}</strong>
        </p>
        <button type="button" class="btn-primary btn-block" @click="handleWithdrawRequest" :disabled="isLoading">출금 요청</button>
      </div>
    </div>

    <!-- ===== 입/출금 내역 영역 ===== -->
    <div class="mypage__wallet-item mypage__wallet-item--column">
      <strong class="mypage__wallet-title">입/출금 내역</strong>
      <div class="mypage__wallet-history">
        <!-- ===== 필터 영역 ===== -->
        <div class="mypage__wallet-history-filter">
          <!-- 타입 필터 버튼 (전체/입금/출금) -->
          <div class="mypage__wallet-history-filter-btns">
            <button
              type="button"
              :class="[
                'btn-sm',
                activeFilter === 'all' ? 'btn-primary' : 'btn-ghost',
              ]"
              @click="handleFilterClick('all')"
            >
              전체
            </button>
            <button
              type="button"
              :class="[
                'btn-sm',
                activeFilter === 'deposit' ? 'btn-primary' : 'btn-ghost',
              ]"
              @click="handleFilterClick('deposit')"
            >
              입금
            </button>
            <button
              type="button"
              :class="[
                'btn-sm',
                activeFilter === 'withdraw' ? 'btn-primary' : 'btn-ghost',
              ]"
              @click="handleFilterClick('withdraw')"
            >
              출금
            </button>
          </div>
          <!-- 날짜 필터 선택 (지난 N일 또는 날짜 범위 선택) -->
          <div class="mypage__wallet-history-filter-select">
            <Select
              ref="selectRef"
              v-model="selectedType"
              :options="options"
              placeholder="전체"
              size="md"
              @change="handleSelectChange"
            />
          </div>
        </div>

        <!-- ===== 테이블 영역 ===== -->
        <div class="table__inner">
          <div class="table__wrap">
            <table class="table table--b">
              <colgroup>
                <col width="200px" />
                <col width="80px" />
                <col width="216px" />
                <col width="216px" />
              </colgroup>
              <thead class="table__head">
                <tr class="table__row table__row--th">
                  <th class="table__cell table__cell--th">날짜</th>
                  <th class="table__cell table__cell--th">구분</th>
                  <th class="table__cell table__cell--th">변경금액</th>
                  <th class="table__cell table__cell--th">잔액</th>
                </tr>
              </thead>
              <tbody class="table__body">
                <tr
                  v-for="transaction in filteredTransactionHistory"
                  :key="transaction.id"
                  class="table__row table__row--td"
                >
                  <!-- 날짜 -->
                  <td class="table__cell table__cell--td">
                    <span class="table__cell-date">{{ transaction.date }}</span>
                  </td>
                  <!-- 구분 (입금/출금, primary/secondary 색상 적용) -->
                  <td
                    class="table__cell table__cell--td"
                    :class="{
                      'table__cell--primary': transaction.type === 'deposit',
                      'table__cell--secondary': transaction.type === 'withdraw',
                    }"
                  >
                    <span>{{
                      transaction.type === "deposit" ? "입금" : "출금"
                    }}</span>
                  </td>
                  <!-- 변경금액 (입금: +, 출금: -, primary/secondary 색상 적용, 원화 포맷) -->
                  <td
                    class="table__cell table__cell--td"
                    :class="{
                      'table__cell--primary': transaction.type === 'deposit',
                      'table__cell--secondary': transaction.type === 'withdraw',
                    }"
                  >
                    <span>
                      {{ transaction.type === "deposit" ? "+" : "-"
                      }}{{ formatKRW(transaction.amount) }}원
                    </span>
                  </td>
                  <!-- 잔액 (원화 포맷) -->
                  <td class="table__cell table__cell--td">
                    <span>{{ formatKRW(transaction.balance) }}원</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<style></style>
